import { useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Truck, Heart, UserPlus, Users, Check, Copy, Trash2, Building2 } from "lucide-react";
import { api, errMsg, useApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [["schedule", CalendarClock, "Schedule"], ["property", Building2, "Property portfolio"], ["supplier", Truck, "Suppliers"], ["family", Heart, "Friends & Family"], ["staff", Users, "Staff"], ["invites", UserPlus, "Invite people"]];
const FIELDS = {
  supplier: { head: "New supplier / supply", detail: ["company", "Company · what they supply"], extra: ["amount", "Amount / terms"], notes: "Notes (orders, follow-ups…)" },
  family: { head: "New friend or family contact", detail: ["relation", "Relationship (brother, friend…)"], extra: ["email", "Email"], notes: "Notes (birthdays, follow-ups…)" },
  property: { head: "New property", detail: ["company", "Location · type (land, residential, commercial…)"], extra: ["amount", "Value / rent"], notes: "Notes (tenants, documents, caretaker…)", phone: "Caretaker / agent phone" },
};
const PARTNER = { despite: "Dr. Despite", sarpong: "Dr. Ofori Sarpong", both: "Both partners" };
const fmt = (s) => new Date(s).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Africa/Accra" });

const useForm = (init) => {
  const [f, setF] = useState(init);
  return [f, (k) => (e) => setF({ ...f, [k]: e.target.value }), () => setF(init)];
};

const Schedule = () => {
  const { data, reload } = useApi("/desk/schedule");
  const [f, set, reset] = useForm({ partner: "both", title: "", with_whom: "", location: "", start: "", remind_minutes: 30, notes: "" });
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/desk/schedule", { ...f, start: new Date(f.start).toISOString(), remind_minutes: Number(f.remind_minutes) });
      toast.success("Added to the schedule");
      reset();
      reload();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const done = async (id) => { await api.patch(`/desk/schedule/${id}`, { status: "done" }); reload(); };
  const list = (data || []).filter((i) => i.status === "upcoming");
  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <form onSubmit={add} className="panel p-5 lg:col-span-2 grid gap-3 h-fit" data-testid="desk-schedule-form">
        <div className="eyebrow">New appointment · reminder</div>
        <select className="field" value={f.partner} onChange={set("partner")} data-testid="desk-partner-select">{Object.entries(PARTNER).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        <input required className="field" placeholder="What (meeting, call, event…)" value={f.title} onChange={set("title")} data-testid="desk-title" />
        <input className="field" placeholder="With whom" value={f.with_whom} onChange={set("with_whom")} data-testid="desk-with" />
        <input className="field" placeholder="Location" value={f.location} onChange={set("location")} data-testid="desk-location" />
        <div className="grid grid-cols-2 gap-3">
          <input required type="datetime-local" className="field" value={f.start} onChange={set("start")} data-testid="desk-start" />
          <select className="field" value={f.remind_minutes} onChange={set("remind_minutes")} data-testid="desk-remind">{[10, 30, 60, 120, 1440].map((m) => <option key={m} value={m}>Remind {m >= 60 ? `${m / 60}h` : `${m} min`} before</option>)}</select>
        </div>
        <textarea className="field" rows={2} placeholder="Notes for the partner" value={f.notes} onChange={set("notes")} data-testid="desk-notes" />
        <button className="btn-gold" data-testid="desk-add-btn">Add & set reminder</button>
      </form>
      <div className="lg:col-span-3 space-y-3" data-testid="desk-schedule-list">
        {list.length === 0 && <p className="text-sm text-ink-3">No upcoming appointments.</p>}
        {list.map((i) => (
          <div key={i.id} className="panel p-4 flex gap-4 items-start" data-testid={`desk-item-${i.id}`}>
            <div className="w-24 shrink-0 font-data text-[11px] text-brand-green">{fmt(i.start)}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-ink">{i.title}{i.with_whom && <span className="font-normal text-ink-3"> · {i.with_whom}</span>}</div>
              <div className="text-xs text-ink-3">{PARTNER[i.partner]}{i.location && ` · ${i.location}`} · reminder {i.remind_minutes} min before · added by {i.created_by}</div>
              {i.notes && <p className="text-xs text-ink-3 mt-1">{i.notes}</p>}
            </div>
            <button onClick={() => done(i.id)} className="btn-ghost !px-3" aria-label="Mark done" data-testid={`desk-done-${i.id}`}><Check size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Book = ({ kind }) => {
  const { data, reload } = useApi(`/desk/book?kind=${kind}`);
  const [f, set, reset] = useForm({ name: "", company: "", phone: "", email: "", relation: "", notes: "", amount: "" });
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/desk/book", { ...f, kind }); reset(); reload(); toast.success("Saved"); } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (id) => { await api.delete(`/desk/book/${id}`); reload(); };
  const F = FIELDS[kind];
  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <form onSubmit={add} className="panel p-5 lg:col-span-2 grid gap-3 h-fit" data-testid={`desk-book-form-${kind}`}>
        <div className="eyebrow">{F.head}</div>
        <input required className="field" placeholder={kind === "property" ? "Property name" : "Name"} value={f.name} onChange={set("name")} data-testid="book-name" />
        <input className="field" placeholder={F.detail[1]} value={f[F.detail[0]]} onChange={set(F.detail[0])} data-testid="book-detail" />
        <div className="grid grid-cols-2 gap-3">
          <input className="field" placeholder={F.phone || "Phone"} value={f.phone} onChange={set("phone")} data-testid="book-phone" />
          <input className="field" placeholder={F.extra[1]} value={f[F.extra[0]]} onChange={set(F.extra[0])} data-testid="book-extra" />
        </div>
        <textarea className="field" rows={2} placeholder={F.notes} value={f.notes} onChange={set("notes")} data-testid="book-notes" />
        <button className="btn-gold" data-testid="book-add-btn">Save</button>
      </form>
      <div className="lg:col-span-3 grid sm:grid-cols-2 gap-3 h-fit">
        {(data || []).map((b) => (
          <div key={b.id} className="panel p-4" data-testid={`book-${b.id}`}>
            <div className="flex justify-between gap-2"><div className="text-sm font-semibold text-ink">{b.name}</div><button onClick={() => del(b.id)} className="text-ink-3 hover:text-red-500" aria-label="Remove" data-testid={`book-del-${b.id}`}><Trash2 size={14} /></button></div>
            <div className="text-xs text-ink-3">{b.company || b.relation}</div>
            <div className="text-xs text-brand-green mt-1">{[b.phone, b.email, b.amount].filter(Boolean).join(" · ")}</div>
            {b.notes && <p className="text-xs text-ink-3 mt-1">{b.notes}</p>}
          </div>
        ))}
        {(data || []).length === 0 && <p className="text-sm text-ink-3">Nothing saved yet.</p>}
      </div>
    </div>
  );
};

const Staff = () => {
  const { data } = useApi("/staff");
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="desk-staff">
      {(data || []).map((s) => <div key={s.id} className="panel p-4"><div className="text-sm font-semibold text-ink">{s.name}</div><div className="text-xs text-ink-3">{s.role} · {s.station}</div><div className="font-data text-[10px] uppercase text-brand-green mt-1">{s.effective_status || s.status}</div></div>)}
    </div>
  );
};

const Invites = () => {
  const { user } = useAuth();
  const { data, reload } = useApi(user.role === "secretary" ? null : "/circle/invites");
  const [f, set, reset] = useForm({ name: "", relation: "", role: "circle" });
  if (user.role === "secretary") return <p className="text-sm text-ink-3">Only Dr. Despite and Dr. Ofori Sarpong can invite people.</p>;
  const link = (t) => `${window.location.origin}/invite/${t}`;
  const copy = (t) => { navigator.clipboard?.writeText(link(t)); toast.success("Invite link copied — send it by WhatsApp, SMS or email"); };
  const add = async (e) => {
    e.preventDefault();
    try { const r = await api.post("/circle/invites", f); reset(); reload(); copy(r.data.token); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <form onSubmit={add} className="panel p-5 lg:col-span-2 grid gap-3 h-fit" data-testid="invite-form">
        <div className="eyebrow">Invite someone</div>
        <input required className="field" placeholder="Their name" value={f.name} onChange={set("name")} data-testid="invite-name" />
        <input className="field" placeholder="Relationship (wife, son, friend, secretary…)" value={f.relation} onChange={set("relation")} data-testid="invite-relation" />
        <select className="field" value={f.role} onChange={set("role")} data-testid="invite-role">
          <option value="circle">Friends & family — private feed</option>
          <option value="secretary">Secretary — manages schedule & reminders</option>
        </select>
        <button className="btn-gold" data-testid="invite-create-btn">Create invite link</button>
        <p className="text-[11px] text-ink-3">They open the link, add their email and phone number and create their own password.</p>
      </form>
      <div className="lg:col-span-3 space-y-2">
        {(data || []).map((i) => (
          <div key={i.id} className="panel p-4 flex items-center gap-3" data-testid={`invite-${i.id}`}>
            <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-ink">{i.name} <span className="font-normal text-ink-3">· {i.relation || i.role}</span></div><div className="text-xs text-ink-3">{i.role === "secretary" ? "Secretary" : "Private circle"} · invited by {i.invited_by} · <span className={i.status === "accepted" ? "text-brand-green" : "text-amber-600"}>{i.status}</span></div></div>
            {i.status === "pending" && <button onClick={() => copy(i.token)} className="btn-ghost !px-3" data-testid={`invite-copy-${i.id}`}><Copy size={14} /> Copy link</button>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Desk() {
  const { user } = useAuth();
  const [tab, setTab] = useState("schedule");
  return (
    <div className="p-5 md:p-8 max-w-7xl" data-testid="partners-desk">
      <div className="eyebrow mb-2">Private · partners, their secretaries & admin</div>
      <h1 className="font-display text-3xl md:text-4xl font-bold text-ink">Partners' Desk</h1>
      <p className="text-sm text-ink-3 mt-1">Welcome, {user.name}. Schedules with reminders, suppliers, friends & family and staff — in one place.</p>
      <div className="flex gap-2 overflow-x-auto mt-6 mb-6 pb-1" data-testid="desk-tabs">
        {TABS.map(([k, I, l]) => <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap border transition-colors ${tab === k ? "bg-brand-green text-white border-brand-green" : "bg-white text-ink border-[color:var(--line)]"}`} data-testid={`desk-tab-${k}`}><I size={14} />{l}</button>)}
      </div>
      {tab === "schedule" && <Schedule />}
      {["supplier", "family", "property"].includes(tab) && <Book key={tab} kind={tab} />}
      {tab === "staff" && <Staff />}
      {tab === "invites" && <Invites />}
    </div>
  );
}
